'use client';
import { useEffect, useRef } from 'react';
import { formatPrice } from '@/utils/currency';
import { pushToDataLayer } from '@/utils/dataLayer';
import { extractIdFromShopifyStr } from '@/utils/helpers';

export const useTrackPlayKitPLPProducts = () => {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    addKitNumbersToElements();
    initIntersectioObservers();
  }, []);
};

function addKitNumbersToElements() {
  // We don't have a kit # field in the Shopify product data so we'll use the display order
  const productCards = document.querySelectorAll(
    '[data-ga-section="explorer-section"] [data-ga-section="product-card"]',
  );
  productCards.forEach(
    (card, i) => ((card as HTMLElement).dataset.gaKitNum = `${i + 1}`),
  );
}

function initIntersectioObservers() {
  const explorerSections = document.querySelectorAll(
    '[data-ga-section="explorer-section"]',
  );

  [...explorerSections].forEach((element, sectionIndex) => {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        detectVisibility(entries, observer, sectionIndex);
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      },
    );
    observer.observe(element);
  });
}

function detectVisibility(
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
  sectionIndex: number,
) {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    const targetElement = entry.target as HTMLElement;

    trackSection(targetElement, sectionIndex);
    observer.unobserve(targetElement);
  });
}

function getProductData(element: HTMLElement) {
  const productCards = element.querySelectorAll(
    '[data-ga-section="product-card"]',
  );

  return [...productCards].map((productCard) => {
    const productCardEl = productCard as HTMLElement;

    const productId = extractIdFromShopifyStr(
      productCardEl.dataset.gaProductId || '',
    );
    const variantId = extractIdFromShopifyStr(
      productCardEl.dataset.gaVariantId || '',
    );
    const priceAmount = Number(productCardEl.dataset.gaPrice);

    return {
      productId,
      variantId,
      currencyCode: productCardEl.dataset.gaCurrencyCode,
      name: productCardEl.dataset.gaName,
      id: productCardEl.dataset.gaId,
      price: formatPrice(priceAmount),
      category: productCardEl.dataset.gaCategory,
      position: productCardEl.dataset.gaKitNum,
      brand: 'Lovevery',
      variantTitle: productCardEl.dataset.gaVariantTitle,
    };
  });
}

function trackSection(element: HTMLElement, sectionIndex: number) {
  const productData = getProductData(element);
  if (!productData.length) return;

  const uaProductData = productData.map((productDataItem) => {
    const { name, id, price, category, brand, position, productId, variantId } =
      productDataItem;

    return {
      name,
      id,
      price,
      category,
      brand,
      position,
      list: `playkits_year${sectionIndex}`,
      productId,
      variantId,
    };
  });

  const uaEvent = {
    event: 'viewItemList',
    ecommerce: {
      currencyCode: productData[0].currencyCode,
      impressions: uaProductData,
    },
  };

  const ga4ProductData = productData.map((productDataItem) => {
    const {
      name,
      id,
      price,
      category,
      brand,
      position,
      productId,
      variantId,
      variantTitle,
    } = productDataItem;

    return {
      item_name: name,
      item_id: id,
      affiliation: `${brand} ${process.env.NEXT_PUBLIC_REGION}`,
      quantity: 1,
      price: price,
      item_category: category,
      item_brand: 'Lovevery',
      item_variant: variantTitle,
      item_list_name: 'playkits',
      item_list_id: `year${sectionIndex}`,
      index: position,
      productId: productId,
      variantId: variantId,
    };
  });

  const ga4Event = {
    event: 'view_item_list',
    ecommerce: {
      currency: productData[0].currencyCode,
      items: ga4ProductData,
    },
  };

  pushToDataLayer(uaEvent);

  // Clear previous ecommerce object before sending to GA4
  pushToDataLayer({ ecommerce: null });
  pushToDataLayer(ga4Event);
}
